import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSave, FiArrowLeft, FiRefreshCw } from "react-icons/fi";
import salaryService from "../../services/salaryService";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;
const round2 = (n) => Math.round(Number(n) * 100) / 100;

const MONTHS = ["", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

// ─── Field helper ─────────────────────────────────────────────────────────────
function SalaryField({ label, name, value, onChange, hint, readOnly = false }) {
    return (
        <div className="form-group">
            <label className="form-label">{label}</label>
            <input
                type="number" min="0" step="0.01"
                name={name} value={value}
                onChange={onChange}
                readOnly={readOnly}
                className="form-control"
                style={readOnly ? { background: "var(--bg)", color: "var(--text-muted)" } : {}}
            />
            {hint && <span className="form-hint">{hint}</span>}
        </div>
    );
}

export default function PayslipEdit() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [record, setRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [syncingAttendance, setSyncingAttendance] = useState(false);

    const [form, setForm] = useState({
        workingDays: 26, presentDays: 0, lopDays: 0, paidLeaveDays: 0,
        basicPay: 0, da: 0, hra: 0, otherAllowances: 0,
        pf: 0, pt: 0, tds: 0, otherDeductions: 0,
        remarks: "", status: "Draft",
    });

    const applyRecordToForm = (d) => {
        setRecord(d);
        setForm({
            workingDays: d.workingDays,
            presentDays: d.presentDays,
            lopDays: d.lopDays,
            paidLeaveDays: d.paidLeaveDays,
            basicPay: d.basicPay,
            da: d.da,
            hra: d.hra,
            otherAllowances: d.otherAllowances,
            pf: d.pf,
            pt: d.pt,
            tds: d.tds,
            otherDeductions: d.otherDeductions,
            remarks: d.remarks,
            status: d.status,
        });
    };

    // ── Load record ───────────────────────────────────────────────────────────
    useEffect(() => {
        const loadRecord = async () => {
            try {
                const res = await salaryService.getById(id);
                const data = res.data;
                applyRecordToForm(data);

                if (data.status !== "Finalized") {
                    setSyncingAttendance(true);
                    try {
                        const refreshed = await salaryService.refreshAttendance(id, data.workingDays);
                        applyRecordToForm(refreshed.data);
                    } finally {
                        setSyncingAttendance(false);
                    }
                }
            } catch (err) {
                toast.error(err.message);
                navigate("/payroll");
            } finally {
                setLoading(false);
            }
        };

        loadRecord();
    }, [id, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    // ── Live computed preview ─────────────────────────────────────────────────
    const grossPay = round2(Number(form.basicPay) + Number(form.da) + Number(form.hra) + Number(form.otherAllowances));
    const dailyRate = form.workingDays > 0 ? round2(grossPay / form.workingDays) : 0;
    const lopDeduction = round2(dailyRate * Number(form.lopDays));
    const totalDeductions = round2(lopDeduction + Number(form.pf) + Number(form.pt) + Number(form.tds) + Number(form.otherDeductions));
    const netPay = round2(grossPay - totalDeductions);

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await salaryService.update(id, form);
            toast.success(res.message);
            navigate(`/payroll/${id}`);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleRefreshAttendance = async () => {
        setSyncingAttendance(true);
        try {
            const res = await salaryService.refreshAttendance(id, form.workingDays);
            applyRecordToForm(res.data);
            toast.success(res.message);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSyncingAttendance(false);
        }
    };

    if (loading) return <div className="loading-center"><div className="spinner" />Loading payslip...</div>;
    if (!record) return null;

    const emp = record.employee;

    return (
        <div>
            {/* ── Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                    <h1 className="page-title">Edit Payslip — {emp?.fullName}</h1>
                    <p className="page-subtitle">
                        {MONTHS[record.month]} {record.year} · {emp?.designation} · {emp?.department}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn btn-outline" onClick={() => navigate(`/payroll/${id}`)}>
                        <FiArrowLeft size={14} /> Back
                    </button>
                    {record?.status !== "Finalized" && (
                        <button className="btn btn-outline" onClick={handleRefreshAttendance} disabled={syncingAttendance}>
                            <FiRefreshCw size={14} /> {syncingAttendance ? "Refreshing..." : "Refresh Attendance"}
                        </button>
                    )}
                    <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                        <FiSave size={14} /> {saving ? "Saving..." : "Save Changes"}
                    </button>
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: "1rem", alignItems: "start" }}>

                {/* ── Left: Form ── */}
                <div style={{ display: "grid", gap: "1rem" }}>

                    {/* Attendance */}
                    <div className="card">
                        <div className="card-header">Attendance for {MONTHS[record.month]}</div>
                        <div className="card-body">
                            <div className="form-grid">
                                <SalaryField label="Working Days (Month Total)" name="workingDays" value={form.workingDays} onChange={handleChange} hint="Total payable days in the month" />
                                <SalaryField label="Present Days" name="presentDays" value={form.presentDays} onChange={handleChange} hint="Auto-filled from attendance" readOnly />
                                <SalaryField label="LOP Days (Loss of Pay)" name="lopDays" value={form.lopDays} onChange={handleChange} hint="Absent + 0.5 × Half Day" />
                                <SalaryField label="Paid Leave Days" name="paidLeaveDays" value={form.paidLeaveDays} onChange={handleChange} hint="On Leave + Medical Leave" />
                            </div>
                        </div>
                    </div>

                    {/* Earnings */}
                    <div className="card">
                        <div className="card-header">Earnings</div>
                        <div className="card-body">
                            <div className="form-grid">
                                <SalaryField label="Basic Pay (₹)" name="basicPay" value={form.basicPay} onChange={handleChange} />
                                <SalaryField label="Dearness Allowance — DA (₹)" name="da" value={form.da} onChange={handleChange} />
                                <SalaryField label="House Rent Allowance — HRA (₹)" name="hra" value={form.hra} onChange={handleChange} />
                                <SalaryField label="Other Allowances (₹)" name="otherAllowances" value={form.otherAllowances} onChange={handleChange} />
                            </div>
                        </div>
                    </div>

                    {/* Deductions */}
                    <div className="card">
                        <div className="card-header">Deductions</div>
                        <div className="card-body">
                            <div className="form-grid">
                                <SalaryField label="Provident Fund — PF (₹)" name="pf" value={form.pf} onChange={handleChange} />
                                <SalaryField label="Professional Tax — PT (₹)" name="pt" value={form.pt} onChange={handleChange} />
                                <SalaryField label="TDS (₹)" name="tds" value={form.tds} onChange={handleChange} />
                                <SalaryField label="Other Deductions (₹)" name="otherDeductions" value={form.otherDeductions} onChange={handleChange} />
                            </div>
                        </div>
                    </div>

                    {/* Remarks & Status */}
                    <div className="card">
                        <div className="card-header">Remarks & Status</div>
                        <div className="card-body">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label className="form-label">Status</label>
                                    <select name="status" value={form.status} onChange={handleChange} className="form-control">
                                        <option value="Draft">Draft</option>
                                        <option value="Finalized">Finalized</option>
                                    </select>
                                </div>
                                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                    <label className="form-label">Remarks (optional)</label>
                                    <textarea name="remarks" value={form.remarks} onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))}
                                        rows={2} className="form-control" style={{ resize: "vertical" }}
                                        placeholder="Any notes about this month's salary..." />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Right: Live Preview ── */}
                <div className="card" style={{ position: "sticky", top: "1rem" }}>
                    <div className="card-header" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <FiRefreshCw size={13} /> Live Preview
                    </div>
                    <div style={{ padding: "1.1rem" }}>

                        {/* Employee info */}
                        <div style={{ marginBottom: "1rem", paddingBottom: "1rem", borderBottom: "1px solid var(--border)" }}>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>{emp?.fullName}</div>
                            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{emp?.employeeCode} · {emp?.department}</div>
                            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{MONTHS[record.month]} {record.year}</div>
                        </div>

                        {/* Attendance */}
                        <div style={{ marginBottom: "1rem" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)", marginBottom: 6 }}>Attendance</div>
                            {[
                                ["Working Days", form.workingDays],
                                ["Present Days", form.presentDays],
                                ["LOP Days", form.lopDays],
                                ["Paid Leave", form.paidLeaveDays],
                            ].map(([label, val]) => (
                                <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "3px 0" }}>
                                    <span style={{ color: "var(--text-muted)" }}>{label}</span>
                                    <span style={{ fontWeight: 500 }}>{val}</span>
                                </div>
                            ))}
                        </div>

                        {/* Earnings */}
                        <div style={{ marginBottom: "1rem" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)", marginBottom: 6 }}>Earnings</div>
                            {[
                                ["Basic Pay", form.basicPay],
                                ["DA", form.da],
                                ["HRA", form.hra],
                                ["Other Allowances", form.otherAllowances],
                            ].map(([label, val]) => (
                                <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "3px 0" }}>
                                    <span style={{ color: "var(--text-muted)" }}>{label}</span>
                                    <span>{inr(val)}</span>
                                </div>
                            ))}
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700, padding: "5px 0", borderTop: "1px solid var(--border)", marginTop: 4 }}>
                                <span>Gross Pay</span>
                                <span style={{ color: "#d97706" }}>{inr(grossPay)}</span>
                            </div>
                        </div>

                        {/* Deductions */}
                        <div style={{ marginBottom: "1rem" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)", marginBottom: 6 }}>Deductions</div>
                            {[
                                ["LOP Deduction", lopDeduction],
                                ["PF", form.pf],
                                ["PT", form.pt],
                                ["TDS", form.tds],
                                ["Other", form.otherDeductions],
                            ].map(([label, val]) => (
                                <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "3px 0" }}>
                                    <span style={{ color: "var(--text-muted)" }}>{label}</span>
                                    <span style={{ color: Number(val) > 0 ? "#dc2626" : "var(--text-muted)" }}>{inr(val)}</span>
                                </div>
                            ))}
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700, padding: "5px 0", borderTop: "1px solid var(--border)", marginTop: 4 }}>
                                <span>Total Deductions</span>
                                <span style={{ color: "#dc2626" }}>{inr(totalDeductions)}</span>
                            </div>
                        </div>

                        {/* Net Pay */}
                        <div style={{ background: netPay >= 0 ? "#f0fdf4" : "#fff1f2", borderRadius: 8, padding: "0.85rem", textAlign: "center" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)", marginBottom: 4 }}>Net Pay</div>
                            <div style={{ fontSize: 24, fontWeight: 800, color: netPay >= 0 ? "#16a34a" : "#dc2626" }}>{inr(netPay)}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
